import { collection, deleteDoc, doc, getDoc, getDocs, setDoc } from "firebase/firestore";
import { v4 as uuidv4 } from "uuid";
import { setPrimary } from "./primaryWrites";

export const cloneTimesheets = async (
  markedTimesheets,
  cloneType,
  fromDbRef,
  toDbRef,
  primaryType,
  vat = false,
) => {
  //Loop through marked items
  markedTimesheets.forEach(async (id) => {
    // Get the data from marked item.

    let markedTsRef = doc(fromDbRef, id);
    let timesheetSnapshot = await getDoc(markedTsRef);

    if (cloneType === "all") {
      let newId = uuidv4();

      // Transfer top level data from marked item document to new location top level.
      // Eg. From a timesheets/timesheet doc to a quotes/quote doc.
      // Keeps the same uid.

      // // Get the jobdescriptions from the marked item.
      await setPrimary(doc(toDbRef, newId), {
        ...timesheetSnapshot.data(),
        primaryType: primaryType,
        created: Date.now(),
        uid: newId,
      });

      // Get the jobdescriptions from the marked item.
      let timesheetJobs = await getDocs(collection(markedTsRef, "jobDescriptions"));

      // Loop through the jobs from the marked item and save them to new location. Keeps the same
      // job description uid.
      timesheetJobs.forEach((job) => {
        // Save to new location
        let newJobId = uuidv4();

        let jRef = doc(toDbRef, newId, "jobDescriptions", newJobId);
        setDoc(jRef, { ...job.data(), uid: newJobId }).catch((err) => {
          console.error("Error setting cloned job desc during cloning process");
        });
      });
    } else if (cloneType === "customer") {
      // Duplicate primary with only customer information

      // Gen new id for duplicated primary
      let newId = uuidv4();

      let sourceData = timesheetSnapshot.data();

      // Carries the old contact log over rather than discarding it (staff have found it
      // genuinely useful context for the same customer's new job) - but tags every old entry
      // with a link back to the primary it actually came from, so history from the previous
      // job doesn't read as if it happened on this one. fromDbRef/toDbRef are always the same
      // collection at every current call site, so fromDbRef.id also tells us where the
      // duplicate itself will live.
      let sourceType = fromDbRef.id.replace(/s$/, "");
      let sourceLabel = sourceType.charAt(0).toUpperCase() + sourceType.slice(1);
      let sourceRefPart = sourceData.refNumber ? ` ${sourceData.refNumber}` : "";
      let sourceLink = `${window.location.origin}/${sourceType}/${sourceData.uid}`;
      let carriedOverCallLog = (sourceData.callLog || []).map((entry) => ({
        ...entry,
        note: `[from ${sourceLabel}${sourceRefPart}](${sourceLink}): ${entry.note}`,
      }));

      // Create new primary with only customer details fields

      await setPrimary(doc(toDbRef, newId), {
        ...sourceData,
        primaryType: primaryType,
        uid: newId,
        bookedInDateTime: "",
        dateSent: "",
        status: "no-status",
        created: Date.now(),
        cert: false,
        comments: "",
        comments2: "",
        jobDayOrderNumber: "",
        jobInBy: null,
        jobStart: "",
        jobDetails: "",
        jobEnd: "",
        jobsheetDateDay: "",
        jobsheetDateMonth: "",
        jobsheetDateTime: "",
        jobsheetReceivedDay: "",
        paid: false,
        quoteAcceptedDay: "",
        quoteAcceptedMonth: "",
        quoteAmount: "",
        quoteSentDay: "",
        quoteSentMonth: "",
        subcontractorPaid: false,
        subcontracted: false,
        sent: false,
        vatMode: vat,
        threadId: "",
        jobTypeCategory: "",
        callLog: carriedOverCallLog,
      });
    }
  });
};

export const permanentPrimaryDelete = async (markedTimesheets, dbRef) => {
  markedTimesheets.forEach(async (id) => {
    let markedTsRef = doc(dbRef, id);

    //Delete job descriptions associated with primary document
    let timesheetJobs = await getDocs(collection(markedTsRef, "jobDescriptions"));

    timesheetJobs.forEach((job) => {
      deleteDoc(doc(markedTsRef, "jobDescriptions", job.data().uid))
        .then(() => {
          console.log("Job from ts deleted successfully");
        })
        .catch((err) => {
          console.error("Error deleting job desc during deletion");
        });
    });

    // Overwrite the primary with a deleted stub rather than deleting it outright. Other devices
    // only sync documents whose modifiedOn has changed, and a hard-deleted document never shows
    // up in that query - so without the stub they'd keep showing it from their cache. Everything
    // that reads primaries skips deleted stubs (see isDeletedStub).
    // TODO: stubs accumulate - build a cleanup that removes ones older than ~6 months.
    let { createdBy = "" } = (await getDoc(markedTsRef)).data() || {};

    await setPrimary(markedTsRef, { uid: id, createdBy, deleted: true })
      .then(() => {
        console.log("Top level primary deleted successfully");
      })
      .catch((err) => {
        console.error("Error deleting top level during deletion");
      });
  });
};

export const deleteJobDescriptions = async (markedItems, primaryId, dbRef) => {
  let markedPrimaryRef = collection(dbRef, primaryId, "jobDescriptions");

  markedItems.forEach((uid) => {
    deleteDoc(doc(markedPrimaryRef, uid)).catch((err) => {
      console.error("Error deleting jobDescriptions from primary");
    });
  });
};

export const transferTimesheets = async (
  markedTimesheets,
  fromDbRef,
  toDbRef,
  primaryType,
  currentPrimaryNumber,
  incPrimaryNumber,
) => {
  // If sending to or from restore, don't need to inc primary number, just swap variables.
  let sendingToOrFromRestore = !currentPrimaryNumber && !incPrimaryNumber;

  //Loop through marked items
  markedTimesheets.forEach(async (id, i) => {
    // Get the data from marked item.
    let markedTsRef = doc(fromDbRef, id);
    let timesheetSnapshot = await getDoc(markedTsRef);

    // Determine whether it is a switch or to the same type: ie - timesheet to timesheet or
    // quote to timesheets.
    let pType = timesheetSnapshot.data().primaryType;
    let refNumber = timesheetSnapshot.data().refNumber ?? "";
    // let worksOrderNumber = timesheetSnapshot.data().worksOrderNumber ?? "";

    let newRefNumber = 0;

    if (sendingToOrFromRestore) {
    } else {
      // Only increment numbers when not transfering to restore.

      // If converting to jobsheet, leave primary number blank.
      if (primaryType === "jobsheet") {
        newRefNumber = "";
      } else {
        // If converting to timesheet or quote, add 1 to current number
        // Let new ref number be 1 added to current primary number. And increment.
        newRefNumber = currentPrimaryNumber + i;
      }

      // This could go in the else block above, but want to keep increasing jobsheet numbers
      // in case we might use them again in the future.
      await incPrimaryNumber(primaryType);
    }

    if (pType !== primaryType) {
      // Swaps the variables if not sending from restore
      // [refNumber, worksOrderNumber] = [worksOrderNumber, refNumber];
    }

    // Transfer top level data from marked item document to new location top level.
    // Eg. From a timesheets/timesheet doc to a quotes/quote doc.
    // Keeps the same uid
    // setPrimary gives the moved copy a fresh modifiedOn (rather than the one copied over from
    // the original) - other devices spot the move by this uid turning up in toDbRef, and drop it
    // from fromDbRef in their cache.
    await setPrimary(doc(toDbRef, id), {
      ...timesheetSnapshot.data(),
      primaryType: primaryType,
      refNumber: sendingToOrFromRestore ? refNumber : newRefNumber,
      // worksOrderNumber,
    });

    // Get the jobdescriptions from the marked item.
    let timesheetJobs = await getDocs(collection(markedTsRef, "jobDescriptions"));

    // Loop through the jobs from the marked item and save them to new location. Keeps the same
    // job description uid.
    timesheetJobs.forEach((job) => {
      // Save to new location

      let jRef = doc(toDbRef, id, "jobDescriptions", job.data().uid);
      setDoc(jRef, job.data())
        .then(() => {
          // Delete from original location
          deleteDoc(job.ref);
        })
        .catch((err) => {
          console.error("Error deleting job desc during timesheet deletion");
        });
    });

    deleteDoc(markedTsRef)
      .then(() => {
        console.log("timesheet deleted successully");
      })
      .catch((err) => {
        console.error(`Error deleting timesheet`);
      });
  });
};

// True for the placeholder left behind by permanentPrimaryDelete.
export const isDeletedStub = (primary) => primary?.deleted === true;
